import { Link } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { motion, useScroll, useTransform } from 'framer-motion'
import {
  ArrowRightIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline'
import { FaYoutube, FaInstagram, FaTiktok } from 'react-icons/fa6'
import { Button } from '@/components/ui/Button'
import PublicHeader from '@/components/layout/PublicHeader'
import LandingFooter from '@/components/landing/LandingFooter'
import { usePageSEO } from '@/hooks/usePageSEO'
import { HeroProductMockup } from '@/components/landing/HeroProductMockup'
import { WorkflowShowcase } from '@/components/landing/WorkflowShowcase'
import { AIIntelligenceSection } from '@/components/landing/AIIntelligenceSection'
import { TestimonialsSection } from '@/components/landing/TestimonialsSection'
import { ViewShowcase } from '@/components/landing/ViewShowcase'
import PricingSection from '@/components/landing/PricingSection'
import { BentoShowcase } from '@/components/landing/BentoShowcase'
import { subscriptionApi } from '@/services/api'
import type { Plan } from '@/types'

import { useTranslation } from 'react-i18next';
// ── Animation variants ────────────────────────────────────────────────────────
const fadeUp: any = {
  hidden: { opacity: 0, y: 28 },
  visible: (delay = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] as const, delay },
  }),
}

const stagger: any = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.09, delayChildren: 0.05 } },
}

// ── Hero Section ──────────────────────────────────────────────────────────────
function HeroSection() {
  const { t } = useTranslation();
  const { scrollY } = useScroll()
  const blobY = useTransform(scrollY, [0, 600], [0, -80])
  const blobScale = useTransform(scrollY, [0, 600], [1, 1.15])

  const valuePropItems = [
    'Plan in Kanban, Calendar, or List',
    'AI generates & scores content',
    '40+ analytics metrics per platform',
    'Publish directly to YouTube, Instagram & TikTok',
  ]

  return (
    <section className="pt-28 lg:pt-32 pb-12 sm:pb-16 px-4 sm:px-6 lg:px-8 relative flex flex-col items-center">
      {/* Parallax ambient blobs */}
      <motion.div
        style={{ y: blobY, scale: blobScale }}
        className="absolute inset-0 pointer-events-none z-0 flex items-center justify-center"
      >
        <div className="absolute top-[10%] w-[800px] h-[800px] bg-primary-500/10 dark:bg-primary-500/5 rounded-full blur-[120px]" />
        <div className="absolute top-[30%] w-[600px] h-[600px] bg-purple-500/10 dark:bg-purple-500/5 rounded-full blur-[100px]" />
        <div className="absolute top-[50%] w-[700px] h-[700px] bg-blue-500/10 dark:bg-blue-500/5 rounded-full blur-[100px]" />
      </motion.div>

      {/* Centered Hero Text */}
      <div className="relative z-10 w-full max-w-5xl mx-auto flex flex-col items-center text-center">
        <motion.div
          variants={stagger}
          initial="hidden"
          animate="visible"
          className="flex flex-col items-center"
        >
          {/* Badge */}
          <motion.div variants={fadeUp} custom={0}>
            <div className="inline-flex items-center space-x-2 bg-white/50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 px-5 py-2 rounded-full text-sm font-semibold mb-8 border border-primary-200/50 dark:border-primary-800 backdrop-blur-sm shadow-sm">
              <motion.span
                animate={{ rotate: [0, 15, -10, 0] }}
                transition={{ duration: 1.2, delay: 1, repeat: Infinity, repeatDelay: 4 }}
                className="text-base"
              >
                🚀
              </motion.span>
              <span>{t('landing.hero2.aiScoringBadge')}</span>
            </div>
          </motion.div>

          {/* Headline */}
          <motion.h1
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-[5rem] font-extrabold text-gray-900 dark:text-white mb-6 sm:mb-8 leading-[1.15] sm:leading-[1.1] tracking-tight"
            variants={fadeUp}
            custom={0.05}
          >{t('landing.hero2.stopUsingFourTools')}<br className="hidden sm:block" />{' '}
            <span className="bg-gradient-to-r from-primary-600 via-purple-600 to-pink-600 dark:from-primary-400 dark:via-purple-400 dark:to-pink-400 bg-clip-text text-transparent drop-shadow-sm">{t('landing.hero2.manageYourContent')}</span>
          </motion.h1>

          {/* Subheadline */}
          <motion.p
            className="text-lg sm:text-xl md:text-2xl text-gray-600 dark:text-gray-400 mb-8 sm:mb-10 leading-relaxed font-medium max-w-3xl px-2 sm:px-0"
            variants={fadeUp}
            custom={0.1}
          >
            PlanWise connects your content calendar to real YouTube, Instagram, and TikTok analytics.
            AI scores your content before you publish, then turns performance data into your next better idea.
          </motion.p>

          {/* Value Props */}
          <motion.div className="flex flex-wrap justify-center gap-x-6 gap-y-3 mb-10 max-w-4xl" variants={stagger}>
            {valuePropItems.map((item, i) => (
              <motion.div
                key={item}
                className="flex items-center gap-2 text-sm sm:text-base font-medium text-gray-700 dark:text-gray-300 bg-white/40 dark:bg-gray-800/40 px-4 py-2 rounded-full backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50"
                variants={fadeUp}
                custom={0.15 + i * 0.05}
              >
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 15, delay: 0.4 + i * 0.07 }}
                >
                  <CheckCircleIcon className="w-5 h-5 text-green-500" />
                </motion.div>
                <span>{item}</span>
              </motion.div>
            ))}
          </motion.div>

          {/* CTA Group */}
          <motion.div className="flex flex-col sm:flex-row gap-4 items-center justify-center mb-6" variants={fadeUp} custom={0.35}>
            <Link to="/register?plan=starter">
              <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}>
                <Button
                  size="lg"
                  className="bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-xl px-10 py-5 text-lg shadow-[0_0_40px_rgba(122,115,243,0.3)] hover:shadow-[0_0_60px_rgba(122,115,243,0.5)] transition-all group border-none"
                  rightIcon={<ArrowRightIcon className="h-5 w-5 group-hover:translate-x-1 transition-transform" />}
                >{t('subscription.pricing.startFreeTrial')}</Button>
              </motion.div>
            </Link>
          </motion.div>

          <motion.p className="text-sm text-gray-500 dark:text-gray-400 mb-6 sm:mb-12 font-medium" variants={fadeUp} custom={0.4}>
            14-day free trial. No credit card required.
          </motion.p>

          {/* Platform icons */}
          <motion.div variants={fadeUp} custom={0.45} className="flex flex-col items-center mb-12 sm:mb-16 md:mb-20 lg:mb-24">
            <p className="text-sm font-bold text-gray-400 dark:text-gray-500 tracking-widest uppercase mb-5">{t('landing.hero2.worksSeamlesslyWith')}</p>
            <div className="flex items-center gap-5">
              {[
                { alt: 'YouTube', Icon: FaYoutube, color: 'text-red-600', hover: 'hover:border-red-500/30 dark:hover:border-red-400/50 hover:shadow-[0_0_20px_rgba(220,38,38,0.2)]' },
                { alt: 'Instagram', Icon: FaInstagram, color: 'text-pink-600', hover: 'hover:border-pink-500/30 dark:hover:border-pink-400/50 hover:shadow-[0_0_20px_rgba(219,39,119,0.2)]' },
                { alt: 'TikTok', Icon: FaTiktok, color: 'text-gray-900 dark:text-white', hover: 'hover:border-gray-500/30 dark:hover:border-gray-400/50 hover:shadow-[0_0_20px_rgba(156,163,175,0.2)]' },
              ].map(({ alt, Icon, color, hover }, i) => (
                <motion.div
                  key={alt}
                  className={`w-14 h-14 rounded-2xl bg-white/80 dark:bg-gray-800/80 border-2 border-gray-100 dark:border-gray-700/50 flex items-center justify-center backdrop-blur-md shadow-sm ${hover} transition-all duration-300 cursor-pointer`}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.7 + i * 0.1, type: 'spring', stiffness: 300 }}
                  whileHover={{ y: -4 }}
                >
                  <Icon className={`w-7 h-7 ${color}`} />
                </motion.div>
              ))}
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* Centered Dashboard Mockup */}
      <motion.div
        className="relative z-10 w-full max-w-[1200px] mx-auto mt-8 sm:mt-10 md:mt-12 px-4 sm:px-6"
        initial={{ opacity: 0, y: 60 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.3 }}
      >
        {/* Glassmorphic Frame with Animated Gradient Border */}
        <div className="relative rounded-[2rem] p-[1px] overflow-hidden shadow-2xl dark:shadow-[0_20px_80px_rgba(0,0,0,0.8)] group w-full">
          {/* Spinning Gradient Border */}
          <div
            className="absolute inset-[-100%] animate-spin z-0 pointer-events-none opacity-50 dark:opacity-100 group-hover:opacity-100 transition-opacity duration-700"
            style={{
              background: 'conic-gradient(from 90deg at 50% 50%, transparent 0%, transparent 85%, rgba(168,85,247,0.4) 95%, rgba(56,189,248,0.9) 100%)',
              animationDuration: '6s'
            }}
          />

          {/* Inner content wrapper */}
          <div className="relative z-10 rounded-[calc(2rem-1px)] p-2 sm:p-4 bg-white/80 dark:bg-[#0B0E14]/95 backdrop-blur-xl w-full h-full flex flex-col">
            {/* Inner Glow */}
            <div className="absolute inset-0 bg-gradient-to-br from-primary-500/5 via-transparent to-purple-500/5 pointer-events-none rounded-[calc(2rem-1px)]" />

            {/* Mockup Container */}
            <div className="relative rounded-2xl sm:rounded-[1.5rem] overflow-hidden bg-gray-50 dark:bg-[#0B0E14] border border-gray-200 dark:border-gray-800 w-full h-[350px] sm:h-[450px] md:h-[500px] lg:h-[600px] xl:h-[700px] shadow-inner">
              {/* We position the mockup absolutely to crop/scale nicely */}
              <div className="absolute top-0 left-0 w-[200vw] sm:w-[150vw] lg:w-full h-[200%] sm:h-[142%] lg:h-full origin-top-left scale-[50%] sm:scale-[70%] lg:scale-100 transition-transform duration-300">
                <HeroProductMockup />
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </section>
  )
}

// ── Main Page ────────────────────────────────────────────────────────────────
export default function LandingPage() {
  const { t } = useTranslation();
  const [plans, setPlans] = useState<Plan[]>([])
  const [isLoadingPlans, setIsLoadingPlans] = useState(true)

  usePageSEO('home', { canonicalPath: '/', ogType: 'website' })

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await subscriptionApi.getPlans()
        setPlans(response.data)
      } catch (error) {
        console.error('Failed to fetch plans:', error)
        setPlans([])
      } finally {
        setIsLoadingPlans(false)
      }
    }
    fetchPlans()
  }, [])

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#0B0E14] font-sans selection:bg-primary-200 selection:text-primary-900 dark:selection:bg-primary-900/50 dark:selection:text-primary-100 overflow-x-hidden relative">
      {/* Global Seamless Ambient Background */}
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        <div className="absolute top-[-10%] left-[-10%] w-[70vw] h-[70vw] bg-primary-400/5 dark:bg-primary-500/5 rounded-full blur-[120px]" />
        <div className="absolute top-[30%] right-[-10%] w-[60vw] h-[60vw] bg-purple-400/5 dark:bg-purple-500/5 rounded-full blur-[120px]" />
        <div className="absolute bottom-[-10%] left-[10%] w-[80vw] h-[80vw] bg-blue-400/5 dark:bg-blue-500/5 rounded-full blur-[120px]" />
      </div>

      <div className="relative z-10">
        <PublicHeader />
        <HeroSection />
        <div id="features" className="scroll-mt-[110px]">
          <WorkflowShowcase />
          <AIIntelligenceSection />
          <ViewShowcase />
          <BentoShowcase />
        </div>
        <TestimonialsSection />
        <PricingSection plans={plans} isLoading={isLoadingPlans} />

        {/* Final CTA */}
        <motion.section
          className="py-24 px-4 sm:px-6 lg:px-8 relative"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6 }}
        >
          <div className="max-w-5xl mx-auto">
            <div className="relative rounded-[2.5rem] overflow-hidden bg-white/60 dark:bg-[#0B0E14]/40 backdrop-blur-[40px] border border-gray-200/50 dark:border-gray-700/50 shadow-2xl p-12 sm:p-20 text-center">
              {/* Inner ambient glow */}
              <div className="absolute inset-0 bg-gradient-to-br from-primary-500/10 via-transparent to-purple-500/10 pointer-events-none" />

              <div className="relative z-10">
                <motion.h2
                  className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-6 tracking-tight"
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                >{t('landing.hero2.readyToSimplify')}</motion.h2>
                <motion.p
                  className="text-lg sm:text-xl text-gray-600 dark:text-gray-300 mb-8 max-w-2xl mx-auto font-medium"
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
                >{t('landing.hero2.planCreateMeasure')}</motion.p>
                <motion.div
                  initial={{ opacity: 0, scale: 0.92 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.45, delay: 0.2, type: 'spring', stiffness: 200 }}
                >
                  <Link to="/register">
                    <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }}>
                      <Button
                        size="lg"
                        className="bg-primary-600 hover:bg-primary-700 text-white font-semibold px-10 py-5 rounded-xl shadow-[0_0_40px_rgba(122,115,243,0.3)] hover:shadow-[0_0_60px_rgba(122,115,243,0.5)] transition-all border-none text-lg group"
                        rightIcon={<ArrowRightIcon className="h-5 w-5 group-hover:translate-x-1 transition-transform" />}
                      >{t('subscription.pricing.startFreeTrial')}</Button>
                    </motion.div>
                  </Link>
                </motion.div>
                <motion.p
                  className="mt-6 text-gray-500 dark:text-gray-400 text-sm font-medium"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: 0.35 }}
                >
                  14-day free trial. No credit card required.
                </motion.p>
              </div>
            </div>
          </div>
        </motion.section>

        <LandingFooter />
      </div>
    </div>
  )
}
